import { c as _c } from "react/compiler-runtime";
import { Field as FormikField, useField, useFormikContext } from 'formik';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useSWR from 'swr';
import { Placement } from '@popperjs/core';
import { TeamRole } from '../../../../../../../typings/TeamMember.interface';
import { withStaticProperties } from '../../../../utils/withStaticProperties';
import { useAuthorized } from '../../../contexts/TeamPermissionContext';
import useDebounce from '../../../hooks/useDebounce';
import Button from '../../base/Button';
import Divider from '../../base/Divider';
import Icon from '../../base/Icon';
import Chip from '../../base/Chip';
import Loading from '../../base/Loading';
import Text from '../../base/Text';
import Dropdown from '../../Dropdown';
import { Div, StyledUtilsProps } from '../../helpers/StyledUtils';
import Search from '../../Search';
import Checkbox from './Checkbox';
import { Variant } from './Input/Input';
const SEARCH_DELAY_MS = 500;
export interface MultiSelectSelectOption {
  value: string;
  label: string;
}
interface Props extends StyledUtilsProps {
  label?: string;
  variant?: Variant;
  outline?: boolean;
  option_title?: string;
  search_placeholder?: string;
  option_monospace?: boolean;
  name: string;
  initial_options: MultiSelectSelectOption[];
  renderToggle?: (opened, toggle, values) => JSX.Element;
  placement?: Placement;
  help?: string;
  required?: boolean;
  disabled?: boolean;
  formatFilters?: (search_term?: string, values?: string[]) => any;
  fetcher?: (filters: object) => Promise<any>;
  fetcherKey?: (filters: object) => string;
  formatOptions?: (data: any) => MultiSelectSelectOption[];
  onClose?: () => void;
  onRemove?: () => void;
  onSubmit?: () => void;
  portal?: boolean;
}
const StyledInput = styled.button`
  border: none;
  &:focus {
    outline: none;
  }
`;
const StyledField = styled(Div)<{
  variant?: Variant;
  custom_toggle?: boolean;
  disabled?: boolean;
}>(({
  theme,
  variant,
  custom_toggle,
  disabled
}) => css`
    width: 100%;
    ${!custom_toggle && css`
      min-width: 276px;
    `};
    &:last-of-type {
      margin-bottom: 0;
    }

    ${StyledInput} {
      padding: ${theme.spacing(1.5)} !important;
      background-color: ${theme.colors.surface.base.surface};
      width: fit-content;
      border-radius: ${theme.radius.normal};
      cursor: text;
      box-sizing: border-box;
      box-shadow: ${theme.shadows.neutral.base};
      width: 100%;
      display: flex;

      &:focus-visible {
        box-shadow: ${theme.shadows.input.focused};
      }

      ${disabled && css`
        background-color: ${theme.colors.surface.base.disabled};
      `}

      > ${Text} {
        margin-left: auto;
      }
    }

    ${variant === 'card' && css`
      > label {
        margin: 0;
        border: 1px solid ${theme.colors.outline.neutral};
        border-bottom: none;
        border-radius: ${theme.radius.normal} ${theme.radius.normal} 0 0;
        padding: ${theme.pxToRem(4)} ${theme.pxToRem(8)};
        background-color: ${theme.colors.surface.base.variant_surface_2};
        font-size: ${theme.font_size[12]};
      }

      ${StyledInput} {
        border-radius: 0 0 ${theme.radius.normal} ${theme.radius.normal};
      }
    `}
  `);
const StyledTitle = styled(Div)`
  background-color: ${({
  theme
}) => theme.colors.surface.base.variant_surface_3};
  border-bottom: 1px solid ${({
  theme
}) => theme.colors.outline.neutral};
`;
export const StyledOption = styled(Checkbox)<Omit<React.HTMLAttributes<HTMLElement>, 'onChange'>>(({
  theme
}) => css`
    font-weight: 500;
    margin: 0;
    cursor: pointer;
    border-radius: ${theme.radius.small};

    &:hover {
      background-color: ${theme.colors.surface.base.hover.neutral};
    }

    label {
      padding: 12px;
      margin: 0;
    }

    span {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  `);
export const StyledOptions = styled(Div)<React.HTMLAttributes<HTMLElement>>`
  overflow-y: auto;
  max-height: 300px;
  border-bottom: 1px solid ${({
  theme
}) => theme.colors.outline.neutral};
  border-top: 1px solid ${({
  theme
}) => theme.colors.outline.neutral};
  padding: ${({
  theme
}) => theme.spacing(1)};
`;
export const StyledLoadingOption = styled(Div)`
  position: sticky;
  bottom: 0;
  background-color: ${({
  theme
}) => theme.colors.surface.base.surface};
`;
const validate = (value: string[], required: boolean) => {
  if (value?.length < 1 && required) return 'Required';
};
const search_initial_state = {
  search_term: '',
  search_term_by_term: {}
};
interface OptionRowProps {
  option: MultiSelectSelectOption;
  checked: boolean;
  mono: boolean;
  size: 's' | 'm';
  onToggle: (e: React.ChangeEvent<HTMLInputElement>, value: string) => void;
}

// One row per option. Memoized so list-wide re-renders (search keystrokes,
// unrelated Formik changes) skip untouched rows, and toggling a value only
// re-renders the row whose `checked` flipped — with 50+ options, re-rendering
// every row on each interaction is what made the picker laggy. Relies on
// `onToggle` being identity-stable (see handleToggleValue).
const MultiSelectOptionRow = memo<OptionRowProps>(t0 => {
  const $ = _c(10);
  const {
    option,
    checked,
    mono,
    size,
    onToggle
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 2,
      y: 1.5
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onToggle || $[2] !== option.value) {
    t2 = e => onToggle(e, option.value);
    $[1] = onToggle;
    $[2] = option.value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== checked || $[5] !== mono || $[6] !== option.label || $[7] !== size || $[8] !== t2) {
    t3 = <StyledOption p={t1} label={option.label} mono={mono} size={size} name={option.label} checked={checked} onChange={t2} />;
    $[4] = checked;
    $[5] = mono;
    $[6] = option.label;
    $[7] = size;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
});
MultiSelectOptionRow.displayName = 'MultiSelectOptionRow';
const MultiSelectInput: React.FC<Props> = props => {
  const {
    label,
    variant,
    option_title,
    search_placeholder,
    name,
    help,
    initial_options,
    required = false,
    onSubmit,
    disabled,
    formatFilters,
    fetcher,
    fetcherKey,
    formatOptions,
    onClose,
    onRemove,
    renderToggle,
    placement,
    option_monospace,
    outline,
    portal,
    ...other_props
  } = props;
  const [field, {
    value: value_0,
    error,
    touched
  }, {
    setValue,
    setTouched
  }] = useField<string[]>({
    name,
    validate: value => validate(value, required)
  });
  const {
    submitCount
  } = useFormikContext();
  const [{
    search_term,
    search_term_by_term
  }, setSearchState] = useState(search_initial_state);

  // Options accumulate from the initial seed + fetches; everything else
  // (lookup maps, filtered list) is derived from them during render.
  const [options, setOptions] = useState<MultiSelectSelectOption[]>(() => initial_options && initial_options.length > 0 ? initial_options : []);
  const debounced_search_term = useDebounce(search_term, SEARCH_DELAY_MS);
  const [should_fetch, setShouldFetch] = useState(false);
  const handleAddOptions = useCallback((new_options: MultiSelectSelectOption[]) => {
    setOptions(prev => {
      const known_values = new Set(prev.map(o => o.value));
      const fresh_options = new_options.filter(o_0 => !known_values.has(o_0.value));
      // Bail with the same reference when the fetch brought nothing new
      // (typical when the list was pre-seeded via initial_options), so no
      // render or downstream memo invalidation happens on open.
      return fresh_options.length > 0 ? [...prev, ...fresh_options] : prev;
    });
  }, []);

  // Always build the fetch through formatFilters — even with an empty search
  // term — so call sites that scope the option list (e.g. connections of one
  // source) keep their filters on the initial open-fetch, not just on search.
  const {
    isValidating
  } = useSWR(should_fetch && fetcher && fetcherKey && formatFilters && fetcherKey(formatFilters(debounced_search_term || undefined)), () => fetcher!(formatFilters!(debounced_search_term || undefined)), {
    onSuccess: data => {
      handleAddOptions(formatOptions!(data));
      setShouldFetch(false);
    }
  });

  // O(n) single-object builds — the previous spread-inside-reduce versions
  // were O(n²) and `values_index_by_value` was rebuilt on every render.
  const options_by_value: {
    [key: string]: MultiSelectSelectOption;
  } = useMemo(() => {
    const by_value: {
      [key: string]: MultiSelectSelectOption;
    } = {};
    for (const o_1 of options) {
      by_value[o_1.value] = o_1;
    }
    return by_value;
  }, [options]);
  useSWR(value_0 && fetcherKey && formatFilters && Object.keys(options_by_value).length === 0 && fetcherKey(formatFilters(undefined, value_0)), () => fetcher!(formatFilters!(undefined, value_0)), {
    onSuccess: data_0 => {
      handleAddOptions(formatOptions!(data_0));
    }
  });
  const values_index_by_value: {
    [key: string]: number;
  } = useMemo(() => {
    const index_by_value: {
      [key: string]: number;
    } = {};
    (value_0 || []).forEach((v, i) => {
      index_by_value[v] = i;
    });
    return index_by_value;
  }, [value_0]);

  // Derived during render instead of stored in state + synced via useEffect —
  // the effect version re-rendered the whole option list twice per keystroke
  // or checkbox toggle.
  const {
    filtered_options,
    all_options_selected
  } = useMemo(() => {
    if (!(value_0?.length > 0) && search_term === '') {
      return {
        filtered_options: options,
        all_options_selected: false
      };
    }
    const lower_search_term = search_term.toLowerCase();
    let all_selected = true;
    const matching_options = options.filter(o_2 => {
      if (!o_2.label.toLowerCase().includes(lower_search_term)) {
        return false;
      }
      if (values_index_by_value[o_2.value] === undefined) {
        all_selected = false;
      }
      return true;
    });
    return {
      filtered_options: matching_options,
      all_options_selected: matching_options.length > 0 ? all_selected : false
    };
  }, [options, search_term, value_0, values_index_by_value]);
  const {
    handleSearch,
    handleAsyncSearch
  } = {
    handleSearch: v_0 => {
      setSearchState(prev_0 => ({
        ...prev_0,
        search_term: v_0
      }));
    },
    handleAsyncSearch: () => {
      setShouldFetch(true);
      setSearchState(prev_1 => ({
        ...prev_1,
        search_term_by_term: {
          ...prev_1.search_term_by_term,
          [search_term]: search_term
        }
      }));
    }
  };
  const handleRemoveValue = (i_0: number) => {
    const new_value = [...value_0];
    new_value.splice(i_0, 1);
    setTouched(true);
    setValue(new_value);
  };

  // Identity-stable (reads the latest state through a ref) so the memoized
  // option rows never re-render just because the handler was recreated.
  const toggle_state_ref = useRef({
    value: value_0,
    values_index_by_value,
    setValue,
    setTouched
  });
  toggle_state_ref.current = {
    value: value_0,
    values_index_by_value,
    setValue,
    setTouched
  };
  const handleToggleValue = useCallback((e: React.ChangeEvent<HTMLInputElement>, v_1: string) => {
    const {
      value: value_1,
      values_index_by_value: values_index_by_value_0,
      setValue: setValue_0,
      setTouched: setTouched_0
    } = toggle_state_ref.current;
    if (e.currentTarget.checked && values_index_by_value_0[v_1] === undefined) {
      setTouched_0(true);
      setValue_0(value_1 ? [...value_1, v_1] : [v_1]);
      return;
    }
    if (!e.currentTarget.checked && values_index_by_value_0[v_1] !== undefined) {
      const new_value_0 = [...value_1];
      new_value_0.splice(values_index_by_value_0[v_1], 1);
      setTouched_0(true);
      setValue_0(new_value_0);
    }
  }, []);
  const handleToggleAll = (e_0: React.ChangeEvent<HTMLInputElement>) => {
    if (e_0.currentTarget.checked && !all_options_selected) {
      const new_values: string[] = [];
      filtered_options.forEach((v_2: MultiSelectSelectOption) => {
        if (values_index_by_value !== undefined && values_index_by_value[v_2.value] !== undefined) {
          return;
        }
        new_values.push(v_2.value);
      });
      setTouched(true);
      setValue(value_0 ? [...value_0, ...new_values] : [...new_values]);
      return;
    }
    if (!e_0.currentTarget.checked && all_options_selected) {
      const filtered_values = new Set(filtered_options.map(o_3 => o_3.value));
      setTouched(true);
      setValue(value_0.filter(v_3 => !filtered_values.has(v_3)));
    }
  };
  useEffect(() => {
    if (search_term === '' || search_term_by_term !== undefined && search_term_by_term[search_term] !== undefined) {
      return;
    }
    handleAsyncSearch();
  }, [debounced_search_term]);
  return <Div m={{
    b: 4
  }} {...other_props}>
      <StyledField variant={variant} custom_toggle={!!renderToggle} disabled={disabled}>
        {label && <label htmlFor={name}>
            <Text semi muted size="xs">
              {label}
            </Text>
          </label>}
        <Dropdown prevent_event_on_children parent_width={{
        min: 408
      }} p={0} disabled portal={portal} onToggle={opened => {
        if (!opened) {
          onClose && onClose();
        }
      }} placement={placement || 'bottom-start'} renderToggle={(opened_0, toggle) => renderToggle ? renderToggle(opened_0, e_1 => {
        toggle(!opened_0, e_1);
        !opened_0 && !should_fetch && setShouldFetch(true);
      }, value_0) : <FormikField as={StyledInput} {...field} onClick={e_2 => {
        if (disabled) return;
        toggle(!opened_0, e_2);
        !opened_0 && !should_fetch && setShouldFetch(true);
      }}>
                {value_0?.length > 0 && fetcher && Object.keys(options_by_value).length === 0 && <Div m={{
          l: 2
        }}>
                    <Loading />
                  </Div>}
                <Div w={100} flex={{
          wrap: true,
          gap: 1
        }} onClick={() => toggle(!opened_0)}>
                  {value_0 && value_0.map((v_4, i_1) => options_by_value[v_4] !== undefined && <Chip key={v_4} label={options_by_value[v_4].label} icon="close" mono={!!fetcher || option_monospace} outline={outline} onClick={e_3 => {
            e_3.preventDefault();
            e_3.stopPropagation();
            handleRemoveValue(i_1);
            onRemove && onRemove();
          }} />)}
                </Div>
                <Text user_select={false} neutral p={{
          l: 2,
          r: 1
        }}>
                  Add
                </Text>
              </FormikField>}>
          {option_title && <StyledTitle p={{
          y: 1,
          x: 3
        }}>
              <Text size="xs" semi text_wrap={false}>
                {option_title}
              </Text>
            </StyledTitle>}
          <Search placeholder={search_placeholder} small borderless onChange={handleSearch} value={search_term} focus />
          {search_term !== '' && filtered_options?.length > 0 && <>
              <Divider />
              <Div>
                <StyledOption flex p={{
              x: 3,
              y: 2
            }} mono={!!fetcher || !!option_monospace} size={!!fetcher || !!option_monospace ? 's' : 'm'} label={<Text muted text_wrap={false} style={{
              display: 'flex',
              minWidth: 0,
              flexGrow: 1
            }}>
                      Add all containing&nbsp;
                      {<Text as={'span'} ellipsis primary>{` "${search_term}"`}</Text>}
                    </Text>} name={''} checked={all_options_selected} onChange={e_4 => handleToggleAll(e_4)} />
              </Div>
            </>}
          <StyledOptions>
            {filtered_options?.length > 0 ? filtered_options.map(o_4 => <MultiSelectOptionRow key={o_4.value} option={o_4} mono={!!fetcher || !!option_monospace} size={!!fetcher || !!option_monospace ? 's' : 'm'} checked={values_index_by_value[o_4.value] !== undefined} onToggle={handleToggleValue} />) : !isValidating ? <Div p={{
            x: 3,
            y: 3
          }}>No option</Div> : null}
            {isValidating && <StyledLoadingOption>
                <Div p={{
              x: 3,
              y: 3.5
            }} flex={{
              justify: 'center',
              align: 'center'
            }}>
                  <Icon right={1} muted small icon="loading" />
                </Div>
              </StyledLoadingOption>}
          </StyledOptions>
          <Div m={3} flex={{
          gap: 2,
          align: 'center',
          justify: 'flex-end'
        }}>
            <Button outline disabled={!value_0 || value_0.length === 0} onClick={() => {
            setTouched(true);
            setValue([]);
          }}>
              Clear all
            </Button>
            {onSubmit && <Button primary disabled={!value_0 || value_0.length === 0} onClick={onSubmit}>
                Apply
              </Button>}
          </Div>
        </Dropdown>
        {help && <Text m={{
        t: 1,
        b: 0
      }} size="xs" as="p" muted>
            <Icon icon="info" left={1} />
            {help}
          </Text>}
      </StyledField>
      {(touched || submitCount > 0) && error && <Text m={{
      t: 1,
      b: 0
    }} as="p" danger>
          {error}
        </Text>}
    </Div>;
};
const MultiSelectInputPermission: React.FC<React.ComponentPropsWithoutRef<typeof MultiSelectInput> & {
  role?: TeamRole;
}> = t0 => {
  const $ = _c(6);
  let props;
  let role;
  if ($[0] !== t0) {
    ({
      role,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = role;
  } else {
    props = $[1];
    role = $[2];
  }
  const authorized = useAuthorized(role);
  const t1 = props.disabled || !authorized;
  let t2;
  if ($[3] !== props || $[4] !== t1) {
    t2 = <MultiSelectInput {...props} disabled={t1} />;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export default withStaticProperties(MultiSelectInput, {
  Permission: MultiSelectInputPermission
});